import { useDispatch, useSelector } from "react-redux";
import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { ReactSVG } from "react-svg";
import imageLinks from "assets/images";
import debounce from "debounce-promise";
import { updateTicketsFilters } from "store/ticketsFilters/actions";
import SelectUI from "components/ui/SelectUI/SelectUI";
import { CustomDatePicker } from "components/ui/InputTypes/CustomDatePicker/CustomDatePicker";
import "./TicketsSearch.scss";
import { apiRoute } from "services";
import API from "services/lib/api";
import { getErrorMessage } from "utils/helper";
import { setAgentsList } from "store/cache/agents/actions";
import ComponentAccessControl from "components/essentials/ComponentAccessControl/ComponentAccessControl";
import {
    TICKET_FILTER_AGENT_PERMISSION,
    TICKET_FILTER_EMAIL_PERMISSION,
} from "components/dashboard/AgentInbox/permissions";

const searchFilters = [
    { filter: "Keyword", placeholder: "Search tickets and messages..." },
    { filter: "Email", placeholder: "Search by email ..." },
    { filter: "Date", placeholder: "Filter by date ..." },
    { filter: "Agent", placeholder: "Search by agent ..." },
];

const searchFilterPermissions = {
    Email: TICKET_FILTER_EMAIL_PERMISSION,
    Agent: TICKET_FILTER_AGENT_PERMISSION,
};

const dateFilterOptions = [
    {
        label: "Any time",
        value: "ANY_TIME",
    },
    {
        label: "Older than a week",
        value: "OLDER_THAN_WEEK",
    },
    {
        label: "Custom range",
        value: "CUSTOM_RANGE",
    },
];

const TicketsSearch = () => {
    const [showFilters, setShowFilters] = useState(false);
    const [currentSearchFilter, setCurrentSearchFilter] = useState(null);
    const [searchValue, setSearchValue] = useState("");

    const [showDateFilter, setShowDateFilter] = useState(false);
    const [showAgentFilter, setShowAgentFilter] = useState(false);
    const [showCustomDateRange, setShowCustomDateRange] = useState(false);
    const dispatch = useDispatch();
    const [errorMssg, setErrorMssg] = useState("");

    // Local state for date filter (not dispatched until search icon is clicked)
    const [localDateFilterType, setLocalDateFilterType] = useState(null);
    const [localDateFrom, setLocalDateFrom] = useState(null);
    const [localDateTo, setLocalDateTo] = useState(null);

    const { ticketsfilterRequest } = useSelector(
        (state) => state.ticketsFilters
    );

    const { agents } = useSelector((state) => state.agents);

    const { dateFilterType, dateFrom, dateTo, agentId, search, keywordSearch } = ticketsfilterRequest;

    // Use ref to prevent unnecessary re-renders from debounced function
    const abortControllerRef = useRef(null);

    // Consolidated function to clear all filters
    const clearAllFilters = useCallback(() => {
        dispatch(updateTicketsFilters({ filterName: "search", filterValue: undefined }));
        dispatch(updateTicketsFilters({ filterName: "keywordSearch", filterValue: undefined }));
        dispatch(updateTicketsFilters({ filterName: "all", filterValue: undefined }));
        dispatch(updateTicketsFilters({ filterName: "dateFilterType", filterValue: undefined }));
        dispatch(updateTicketsFilters({ filterName: "dateFrom", filterValue: undefined }));
        dispatch(updateTicketsFilters({ filterName: "dateTo", filterValue: undefined }));
        dispatch(updateTicketsFilters({ filterName: "agentId", filterValue: undefined }));
    }, [dispatch]);

    const handleRemoveSearchFilter = useCallback((e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        setCurrentSearchFilter(null);
        setSearchValue("");
        setShowAgentFilter(false);
        setShowDateFilter(false);
        setShowCustomDateRange(false);
        setLocalDateFilterType(null);
        setLocalDateFrom(null);
        setLocalDateTo(null);
        clearAllFilters();
    }, [clearAllFilters]);

    const formatDateRangeDisplay = useCallback((from, to) => {
        if (from && to) {
            const startDate = new Date(from);
            const endDate = new Date(to);
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
        }
        return "";
    }, []);

    const handleSetSearchFilter = useCallback((searchFilter) => {
        const { filter } = searchFilter;
        setCurrentSearchFilter(searchFilter);

        // Reset all filter UI states
            setShowDateFilter(false);
            setShowAgentFilter(false);
            setShowCustomDateRange(false);

        if (filter === "Keyword") {
            setShowFilters(false);
            setSearchValue(keywordSearch || "");
        } else if (filter === "Email") {
            setShowFilters(false);
            setSearchValue(search || "");
        } else if (filter === "Agent") {
            setShowAgentFilter(true);
            const selectedAgent = agents?.find(({ value }) => value === agentId);
            setSearchValue(selectedAgent?.label || "");
        } else if (filter === "Date") {
            setShowDateFilter(true);
            const currentDateFilterType = dateFilterType || "ANY_TIME";
            setShowCustomDateRange(currentDateFilterType === "CUSTOM_RANGE");
            
            setLocalDateFilterType(currentDateFilterType);
            setLocalDateFrom(dateFrom || null);
            setLocalDateTo(dateTo || null);
            
            if (currentDateFilterType === "CUSTOM_RANGE" && dateFrom && dateTo) {
                setSearchValue(formatDateRangeDisplay(dateFrom, dateTo));
            } else {
                const option = dateFilterOptions.find(({ value }) => value === currentDateFilterType);
                setSearchValue(option?.label || "Any time");
            }
        }
    }, [keywordSearch, search, agentId, agents, dateFilterType, dateFrom, dateTo, formatDateRangeDisplay]);

    const handleDateFilterSelect = useCallback((label, value) => {
        const isCustomRange = value === "CUSTOM_RANGE";
        setShowCustomDateRange(isCustomRange);
        
        if (value === "ANY_TIME") {
            setLocalDateFilterType("ANY_TIME");
            setLocalDateFrom(null);
            setLocalDateTo(null);
            setSearchValue("Any time");
        } else {
            setLocalDateFilterType(value);
            if (value !== "CUSTOM_RANGE") {
                setLocalDateFrom(null);
                setLocalDateTo(null);
                setSearchValue(label);
            } else {
                if (localDateFrom && localDateTo) {
                    setSearchValue(formatDateRangeDisplay(localDateFrom, localDateTo));
                } else {
                    setSearchValue("Custom range");
                }
            }
        }
    }, [formatDateRangeDisplay, localDateFrom, localDateTo]);

    const handleBackToFilters = useCallback(() => {
        setShowCustomDateRange(false);
        setShowDateFilter(true);
    }, []);

    const handleClearDateRange = useCallback(() => {
        setLocalDateFrom(null);
        setLocalDateTo(null);
        setSearchValue("Custom range");
    }, []);

    const handleCustomDateRangeChange = useCallback((dates) => {
        if (dates && dates[0] && dates[1]) {
            const startDate = dates[0].toISOString().split('T')[0];
            const endDate = dates[1].toISOString().split('T')[0];
            setLocalDateFrom(startDate);
            setLocalDateTo(endDate);
            setLocalDateFilterType("CUSTOM_RANGE");
            setSearchValue(formatDateRangeDisplay(startDate, endDate));
        } else if (dates && dates[0] && !dates[1]) {
            const startDate = dates[0].toISOString().split('T')[0];
            setLocalDateFrom(startDate);
            setLocalDateTo(null);
            setLocalDateFilterType("CUSTOM_RANGE");
            const startDateFormatted = new Date(startDate).toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'short', 
                day: 'numeric' 
            });
            setSearchValue(`${startDateFormatted} - ...`);
        } else {
            setLocalDateFrom(null);
            setLocalDateTo(null);
            setSearchValue("Custom range");
        }
    }, [formatDateRangeDisplay]);

    const handleSelect = useCallback((label, value, isDate) => {
        if (isDate) {
            handleDateFilterSelect(label, value);
        } else {
            setSearchValue(label);
            // Clear other filters and set agent
            dispatch(updateTicketsFilters({ filterName: "search", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "keywordSearch", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateFilterType", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateFrom", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateTo", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "agentId", filterValue: value }));
            dispatch(updateTicketsFilters({ filterName: "page", filterValue: 1 }));
            setShowFilters(false);
        }
    }, [dispatch, handleDateFilterSelect]);

    // Unified search handler with consistent logic
    const handleSearch = useCallback((value) => {
        const filterType = currentSearchFilter?.filter;
        
        // Always reset to page 1 and clear agent filter
        dispatch(updateTicketsFilters({ filterName: "page", filterValue: 1 }));
        dispatch(updateTicketsFilters({ filterName: "agentId", filterValue: undefined }));
        
        if (filterType === "Keyword") {
            dispatch(updateTicketsFilters({ filterName: "keywordSearch", filterValue: value }));
            dispatch(updateTicketsFilters({ filterName: "search", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateFilterType", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateFrom", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateTo", filterValue: undefined }));
        } else if (filterType === "Date") {
            // Clear text-based searches
            dispatch(updateTicketsFilters({ filterName: "search", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "keywordSearch", filterValue: undefined }));
            
            // Apply date filter
            if (localDateFilterType === "ANY_TIME") {
                dispatch(updateTicketsFilters({ filterName: "dateFilterType", filterValue: undefined }));
                dispatch(updateTicketsFilters({ filterName: "dateFrom", filterValue: undefined }));
                dispatch(updateTicketsFilters({ filterName: "dateTo", filterValue: undefined }));
            } else {
                dispatch(updateTicketsFilters({ filterName: "dateFilterType", filterValue: localDateFilterType }));
                if (localDateFilterType === "CUSTOM_RANGE" && localDateFrom && localDateTo) {
                    dispatch(updateTicketsFilters({ filterName: "dateFrom", filterValue: localDateFrom }));
                    dispatch(updateTicketsFilters({ filterName: "dateTo", filterValue: localDateTo }));
                } else {
                    dispatch(updateTicketsFilters({ filterName: "dateFrom", filterValue: undefined }));
                    dispatch(updateTicketsFilters({ filterName: "dateTo", filterValue: undefined }));
                }
            }
        } else {
            // Email search
            dispatch(updateTicketsFilters({ filterName: "search", filterValue: value }));
            dispatch(updateTicketsFilters({ filterName: "keywordSearch", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateFilterType", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateFrom", filterValue: undefined }));
            dispatch(updateTicketsFilters({ filterName: "dateTo", filterValue: undefined }));
        }
        
        dispatch(updateTicketsFilters({ filterName: "all", filterValue: true }));
    }, [currentSearchFilter, localDateFilterType, localDateFrom, localDateTo, dispatch]);

    const handleApplyDateFilter = useCallback(() => {
        if (localDateFilterType === "CUSTOM_RANGE" && localDateFrom && localDateTo) {
            handleSearch(searchValue);
            setShowCustomDateRange(false);
            setShowFilters(false);
        }
    }, [localDateFilterType, localDateFrom, localDateTo, searchValue, handleSearch]);

    const debouncedSearch = useMemo(
        () => debounce((search) => handleSearch(search), 1000, { leading: true }),
        [handleSearch]
    );

    const handleChange = useCallback(({ target: { value } }) => {
        setSearchValue(value);
        if (currentSearchFilter?.filter === "Email") {
            debouncedSearch(value);
        }
    }, [currentSearchFilter, debouncedSearch]);

    const getAgents = useCallback(async (httpRequest) => {
        try {
            setErrorMssg("");
            const url = apiRoute.getUsers;
            const res = await API.get(url, {
                params: { all: true },
                signal: httpRequest?.signal,
            });

            if (res.status === 200) {
                const { data } = res.data;
                dispatch(
                    setAgentsList(
                        data?.map(
                            ({ firstName, lastName, userId, ...rest }) => ({
                                    value: userId,
                                    label: `${firstName} ${lastName}`,
                                    firstName,
                                    lastName,
                                    userId,
                                    ...rest,
                            })
                        )
                    )
                );
            }
        } catch (err) {
            if (!httpRequest?.signal?.aborted) {
            setErrorMssg(getErrorMessage(err));
            }
        }
    }, [dispatch]);

    // Sync local date filter state with Redux when it changes externally
    useEffect(() => {
        if (currentSearchFilter?.filter === "Date") {
            const currentType = dateFilterType || "ANY_TIME";
            setLocalDateFilterType(currentType);
            setLocalDateFrom(dateFrom || null);
            setLocalDateTo(dateTo || null);
            
            if (currentType === "CUSTOM_RANGE" && dateFrom && dateTo) {
                setSearchValue(formatDateRangeDisplay(dateFrom, dateTo));
            } else if (dateFilterType) {
                const option = dateFilterOptions.find(({ value }) => value === dateFilterType);
                setSearchValue(option?.label || "Any time");
            } else {
                setSearchValue("Any time");
            }
        }
    }, [dateFilterType, dateFrom, dateTo, currentSearchFilter, formatDateRangeDisplay]);

    // Auto-detect active filter from Redux state
    useEffect(() => {
        if (keywordSearch?.trim()) {
            if (currentSearchFilter?.filter !== "Keyword") {
                handleSetSearchFilter({ filter: "Keyword", placeholder: "Search tickets and messages..." });
            }
        } else if (search?.trim()) {
            if (currentSearchFilter?.filter !== "Email") {
                handleSetSearchFilter({ filter: "Email", placeholder: "Search by email ..." });
            }
        } else if (agentId) {
            if (currentSearchFilter?.filter !== "Agent") {
                handleSetSearchFilter({ filter: "Agent", placeholder: "Search by agent ..." });
            }
        } else if (dateFilterType || (currentSearchFilter?.filter === "Date" && !dateFilterType)) {
            if (currentSearchFilter?.filter !== "Date") {
                handleSetSearchFilter({ filter: "Date", placeholder: "Filter by date ..." });
            }
        }
    }, [agentId, dateFilterType, search, keywordSearch, currentSearchFilter, handleSetSearchFilter]);

    useEffect(() => {
        abortControllerRef.current = new AbortController();
        getAgents(abortControllerRef.current);
        
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [getAgents]);

    // Optimized click handlers with proper event handling
    const handleToggleFilters = useCallback((e) => {
        if (e) {
            e.stopPropagation();
        }
        setShowFilters(prev => !prev);
    }, []);

    const handleSearchIconClick = useCallback((e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        
        const filterType = currentSearchFilter?.filter;
        const isDateSearch = filterType === "Date";
        const isKeywordSearch = filterType === "Keyword";
        const isEmailSearch = filterType === "Email";
        
        if (isDateSearch && localDateFilterType) {
            if (localDateFilterType === "CUSTOM_RANGE" && (!localDateFrom || !localDateTo)) {
                return; // Don't apply incomplete custom range
            }
            handleSearch(searchValue);
        } else if ((isKeywordSearch || isEmailSearch) && searchValue?.trim().length > 0) {
            handleSearch(searchValue);
        }
    }, [currentSearchFilter, localDateFilterType, localDateFrom, localDateTo, searchValue, handleSearch]);

    const handleInputClick = useCallback((e) => {
        e.stopPropagation();
    }, []);

    const handleFilterClick = useCallback((searchFilter) => {
        handleSetSearchFilter(searchFilter);
    }, [handleSetSearchFilter]);

    // Compute input value
    const inputValue = useMemo(() => {
        if (showCustomDateRange && ((localDateFrom && localDateTo) || (dateFrom && dateTo))) {
            return formatDateRangeDisplay(localDateFrom || dateFrom, localDateTo || dateTo);
        }
        return searchValue;
    }, [showCustomDateRange, localDateFrom, localDateTo, dateFrom, dateTo, searchValue, formatDateRangeDisplay]);

    const isInputDisabled = !currentSearchFilter || showDateFilter || showAgentFilter || showCustomDateRange;

    return (
        <div className='tickets__search__container me-auto' onClick={(e) => e.stopPropagation()}>
            <div className='tickets__search' onClick={handleToggleFilters}>
                <span className='tickets__text'>Tickets;</span>

                {currentSearchFilter && (
                    <div className='current__search__filter'>
                        <span className='current__search__filter__text'>
                            {currentSearchFilter?.filter}
                        </span>
                        <img
                            src={imageLinks?.icons?.cancelX}
                            className='remove__filter'
                            alt='Cancel Icon'
                            onClick={handleRemoveSearchFilter}
                        />
                    </div>
                )}
                <div className='tickets__search__input__field'>
                    <input
                        className='tickets__search__input'
                        placeholder={currentSearchFilter?.placeholder || "Search..."}
                        onClick={handleInputClick}
                        value={inputValue}
                        onChange={handleChange}
                        disabled={isInputDisabled}
                    />
                </div>
                <div className='tickets__search__actions'>
                    <ReactSVG
                        src={imageLinks?.icons?.funnelFilter}
                        onClick={handleToggleFilters}
                    />
                    <ReactSVG
                        src={imageLinks?.icons?.search}
                        onClick={handleSearchIconClick}
                    />
                </div>
            </div>
            {showFilters && (
                <div className='tickets__search__filters'>
                    {!showCustomDateRange && (
                        <span className='tickets__search__filters__text'>
                            Filter
                        </span>
                    )}
                    <div className='tickets__search__filters__list'>
                        {!showCustomDateRange && searchFilters?.map((searchFilter) => (
                            <ComponentAccessControl
                                baseResources={
                                    searchFilterPermissions[searchFilter.filter]
                                }
                                key={searchFilter.filter}>
                                <div
                                    className={`tickets__search__filter ${
                                        searchFilter?.filter ===
                                        currentSearchFilter?.filter
                                            ? "tickets__search__filter__active"
                                            : ""
                                    }`}
                                    onClick={() => handleFilterClick(searchFilter)}>
                                    {searchFilter?.filter}
                                </div>
                            </ComponentAccessControl>
                        ))}
                        {showDateFilter && !showCustomDateRange && (
                            <SelectUI
                                otherClassNames='tickets__search__date__filter'
                                placeholder="Filter by"
                                options={dateFilterOptions}
                                value={dateFilterOptions?.filter(({ value }) =>
                                    value === (localDateFilterType || dateFilterType || "ANY_TIME")
                                )}
                                onChange={({ value, label }) =>
                                    handleDateFilterSelect(label, value)
                                }
                                error={Boolean(errorMssg)}
                            />
                        )}
                        {showCustomDateRange && (
                            <div className='tickets__search__custom__date__range__modal'>
                                <div className='tickets__search__custom__date__range__header'>
                                    <h3 className='tickets__search__custom__date__range__title'>
                                        Select Custom Date Range
                                    </h3>
                                    <button
                                        className='tickets__search__custom__date__range__close'
                                        onClick={handleBackToFilters}
                                        type="button"
                                    >
                                        <ReactSVG src={imageLinks?.icons?.cancelX} />
                                    </button>
                                </div>
                                
                                <div className='tickets__search__custom__date__range__inputs'>
                                    <div className='tickets__search__custom__date__range__input__wrapper'>
                                        <label className='tickets__search__custom__date__range__input__label'>
                                            Start Date
                                        </label>
                                        <input
                                            type="text"
                                            className='tickets__search__custom__date__range__input'
                                            value={
                                                localDateFrom || dateFrom
                                                    ? new Date(localDateFrom || dateFrom).toLocaleDateString('en-US', {
                                                          year: 'numeric',
                                                          month: '2-digit',
                                                          day: '2-digit'
                                                      })
                                                    : ''
                                            }
                                            readOnly
                                            placeholder="MM/DD/YYYY"
                                        />
                                    </div>
                                    <div className='tickets__search__custom__date__range__arrow'>
                                        <ReactSVG src={imageLinks?.icons?.tinyArrowRight} />
                                    </div>
                                    <div className='tickets__search__custom__date__range__input__wrapper'>
                                        <label className='tickets__search__custom__date__range__input__label'>
                                            End Date
                                        </label>
                                        <input
                                            type="text"
                                            className='tickets__search__custom__date__range__input'
                                            value={
                                                localDateTo || dateTo
                                                    ? new Date(localDateTo || dateTo).toLocaleDateString('en-US', {
                                                          year: 'numeric',
                                                          month: '2-digit',
                                                          day: '2-digit'
                                                      })
                                                    : ''
                                            }
                                            readOnly
                                            placeholder="MM/DD/YYYY"
                                        />
                                    </div>
                                </div>

                                <p className='tickets__search__custom__date__range__instruction'>
                                    Select your start date from the calendar
                                </p>

                                <div className='tickets__search__custom__date__range__calendar'>
                                    <CustomDatePicker
                                        onChange={handleCustomDateRangeChange}
                                        placeholder="Select date range"
                                        startDate={(localDateFrom || dateFrom) ? new Date(localDateFrom || dateFrom) : null}
                                        endDate={(localDateTo || dateTo) ? new Date(localDateTo || dateTo) : null}
                                        selectsRange
                                        otherClassNames="tickets__search__date__picker"
                                        inline={true}
                                    />
                                </div>

                                <div className='tickets__search__custom__date__range__actions'>
                                    <button
                                        className='tickets__search__custom__date__range__clear'
                                        onClick={handleClearDateRange}
                                        type="button"
                                    >
                                        Clear
                                    </button>
                                    <button
                                        className='tickets__search__custom__date__range__apply'
                                        onClick={handleApplyDateFilter}
                                        disabled={!localDateFrom || !localDateTo}
                                        type="button"
                                    >
                                        Apply filter
                                    </button>
                                </div>
                            </div>
                        )}
                        {showAgentFilter && (
                            <ComponentAccessControl
                                baseResources={TICKET_FILTER_AGENT_PERMISSION}>
                                <SelectUI
                                    otherClassNames='tickets__search__date__filter'
                                    placeholder="Select Agent"
                                    options={agents}
                                    value={agents?.filter(({ value }) =>
                                        value === agentId
                                    )}
                                    onChange={({ value, label }) =>
                                        handleSelect(label, value, false)
                                    }
                                    error={Boolean(errorMssg)}
                                />
                            </ComponentAccessControl>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TicketsSearch;
